/**
 * 経理担当への自動通知 (2026-10-26)。
 *
 * 営業・通知設定 > 自動通知 > 経理担当 で、通知のきっかけ (トリガー) ごとに
 * メール / Google Chat / Chatwork のどれで送るかを選ぶ。
 *
 * 保存先: settings/sales
 *  - accountingEmail                 … メールの宛先 (既存)
 *  - accountingGoogleChatEndpointId  … Google Chat の宛先 (chat_endpoints, kind=google_chat)
 *  - accountingChatworkEndpointId    … Chatwork の宛先   (chat_endpoints, kind=chatwork)
 *  - accountingNotify[trigger]       … { mail, googleChat, chatwork }
 *
 * 未設定のトリガーは「メールのみ」(この機能を入れる前の動作と同じ)。
 *
 * チャット用の文面はこのファイルの組み立て関数で作る。サーバー (実際の送信) と
 * 設定画面 (「i」アイコンの送信例) が同じ関数を使うため、送信例と実際の文面は一致する。
 */
import { formatMoney, roundMoney } from '../utils/money';

/** 通知用の金額表記。小数があるときは 2 桁にそろえる (€865.8 → €865.80)。整数はそのまま (€3,200)。 */
function noticeMoney(amount: number, currency: string): string {
  const s = formatMoney(roundMoney(amount, currency), currency);
  return /\.\d$/.test(s) ? `${s}0` : s;
}

export type AccountingNotifyTrigger =
  | 'transfer_request_added'
  | 'transfer_request_batch'
  | 'commission_payment_request'
  | 'commission_invoice_received'
  | 'sa_renewal_due_internal';

export interface AccountingNotifyChannels {
  mail: boolean;
  googleChat: boolean;
  chatwork: boolean;
}

export type AccountingNotifyConfig = Partial<
  Record<AccountingNotifyTrigger, Partial<AccountingNotifyChannels>>
>;

/** 設定画面の表示順・文言。 */
export const ACCOUNTING_NOTIFY_TRIGGERS: Array<{
  id: AccountingNotifyTrigger;
  label: string;
  /** いつ送られるか。 */
  when: string;
  /** メールで送るときの文面の出どころ。 */
  mailSource: string;
}> = [
  {
    id: 'transfer_request_added',
    label: '新たな送金依頼を追加したとき',
    when: '商談詳細 > 発注 の「送金依頼追加」を押したとき',
    mailSource: 'チャットと同じ内容（お知らせである旨の 1 行入り。送金のお願いではない）',
  },
  {
    id: 'transfer_request_batch',
    label: '現在の送金依頼を経理に連絡するとき',
    when: '商談ボード / 経理 > 送金依頼 の「送金依頼を送信」を押したとき（カテゴリーごとに 1 通）',
    mailSource: 'チャットと同じ内容',
  },
  {
    id: 'commission_payment_request',
    label: '報酬の支払い申請',
    when: '経理 > 報酬台帳 の「支払い申請」(メールのアイコン) を押したとき',
    mailSource: '業務通知テンプレート「【報酬】支払い申請 (経理宛)」',
  },
  {
    id: 'commission_invoice_received',
    label: 'SA 請求書の受領',
    when: 'SA が報酬の請求書を作成したとき',
    mailSource: '業務通知テンプレート「【報酬】SA 請求書受領 (経理宛)」',
  },
  {
    id: 'sa_renewal_due_internal',
    label: 'SA 契約更新の対応',
    when: 'SA の契約終了が近づいたとき / SA が契約更新を申請したとき',
    mailSource: '業務通知テンプレート「【SA】更新対応のお知らせ (管理者宛)」',
  },
];

export const ACCOUNTING_NOTIFY_CHANNEL_LABELS: Record<keyof AccountingNotifyChannels, string> = {
  mail: 'メール',
  googleChat: 'GC',
  chatwork: 'CW',
};

/** 未設定のトリガーの既定 (この機能を入れる前と同じ「メールのみ」)。 */
export const ACCOUNTING_NOTIFY_DEFAULT_CHANNELS: AccountingNotifyChannels = {
  mail: true,
  googleChat: false,
  chatwork: false,
};

export function resolveAccountingNotifyChannels(
  config: AccountingNotifyConfig | null | undefined,
  trigger: AccountingNotifyTrigger,
): AccountingNotifyChannels {
  const c = config?.[trigger];
  return {
    mail: c?.mail ?? ACCOUNTING_NOTIFY_DEFAULT_CHANNELS.mail,
    googleChat: c?.googleChat ?? ACCOUNTING_NOTIFY_DEFAULT_CHANNELS.googleChat,
    chatwork: c?.chatwork ?? ACCOUNTING_NOTIFY_DEFAULT_CHANNELS.chatwork,
  };
}

// ─────────────────────────────────────────────
// 文面の組み立て
// ─────────────────────────────────────────────

/** チャネル別の文面。 */
export interface AccountingNoticeText {
  /** メール件名 (メールを自前の文面で送るトリガー用)。 */
  subject: string;
  /** Chatwork 記法 ([title] / [hr])。 */
  chatwork: string;
  /** Google Chat (*太字*)。 */
  googleChat: string;
  /** メール本文 (記法なし)。 */
  plain: string;
}

const PLAIN_HR = '────────────────';

interface NoticeParts {
  subject: string;
  title: string;
  /** 例: "▼ 確認はこちら" */
  linkLabel: string;
  url: string;
  /** [hr] と [hr] の間に入る本文。 */
  body: string;
  /** 最後の [hr] の後ろに入る結び (任意)。 */
  footer?: string;
  /** メール本文だけ、タイトルの直後に入れる注記 (任意)。 */
  mailNote?: string;
}

function renderNotice(p: NoticeParts): AccountingNoticeText {
  const link = p.url ? `${p.linkLabel}\n${p.url}\n\n` : '';
  const footer = p.footer ? `\n\n${p.footer}` : '';
  // Chatwork: 全体を [info]…[/info] で囲み、末尾の [hr] は置かない (2026-10-26)。
  // 結び (footer) があるときだけ、本文との間に [hr] を残す。
  const cwFooter = p.footer ? `\n[hr]\n\n${p.footer}` : '';
  return {
    subject: p.subject,
    chatwork: `[info][title]${p.title}[/title]\n\n${link}[hr]\n${p.body}${cwFooter}\n[/info]`,
    googleChat: `*${p.title}*\n\n${link}${PLAIN_HR}\n${p.body}\n${PLAIN_HR}${footer}`,
    plain: `${p.title}${p.mailNote ? `\n${p.mailNote}` : ''}\n\n${link}${PLAIN_HR}\n${p.body}\n${PLAIN_HR}${footer}`,
  };
}

/** 送金依頼 1 件分 (transfer_requests のスナップショットから取る値)。 */
export interface TransferNoticeItem {
  customerName: string;
  invoiceNumber: string | null;
  finalAmount: number;
  currency: string;
}

/** "TimeWaver" → "TimeWaver社"。カテゴリー未設定は "メーカー"。 */
export function transferMakerLabel(categoryName: string | null | undefined): string {
  const name = (categoryName ?? '').trim();
  return name ? `${name}社` : 'メーカー';
}

/** 「経理 > 送金依頼」画面の URL。 */
export function transferRequestsAdminUrl(adminBaseUrl: string): string {
  return adminBaseUrl ? `${adminBaseUrl.replace(/\/$/, '')}/finance?hub=transfers` : '';
}

/** ① 〜 ㊿。51 件目以降は "(51)"。 */
export function circledNumber(n: number): string {
  if (n >= 1 && n <= 20) return String.fromCodePoint(0x2460 + n - 1);
  if (n >= 21 && n <= 35) return String.fromCodePoint(0x3251 + n - 21);
  if (n >= 36 && n <= 50) return String.fromCodePoint(0x32b1 + n - 36);
  return `(${n})`;
}

function itemLines(item: TransferNoticeItem): string {
  const name = item.customerName || '(顧客名なし)';
  const inv = item.invoiceNumber ? `(${item.invoiceNumber})` : '';
  return `${name}様${inv}\n金額 ${noticeMoney(item.finalAmount, item.currency)}`;
}

/** 通貨ごとに合計して "€19,326.73" / "€100 / ¥2,000" の形にする。 */
export function formatTransferTotal(items: TransferNoticeItem[]): string {
  const totals = new Map<string, number>();
  for (const it of items) {
    totals.set(it.currency, (totals.get(it.currency) ?? 0) + (it.finalAmount || 0));
  }
  // 合計は通貨の桁数で丸める (浮動小数の誤差 0.1 + 0.2 等を除く)
  return Array.from(totals.entries())
    .map(([cur, sum]) => noticeMoney(sum, cur))
    .join(' / ');
}

/**
 * 「新たな送金依頼を追加したとき」の文面 (チャット・メール共通)。
 * 送金のお願いではなく「一覧に追加された」お知らせ。送金のお願いは「送金依頼を送信」で送る。
 */
export function buildTransferAddedNotice(args: {
  categoryName: string | null;
  item: TransferNoticeItem;
  url: string;
}): AccountingNoticeText {
  const maker = transferMakerLabel(args.categoryName);
  return renderNotice({
    subject: `【送金依頼追加】${maker} ${args.item.customerName}様`,
    title: `💸${maker}への送金依頼が追加されました。`,
    mailNote:
      '※ このメールはお知らせです。送金のお願いは「送金依頼を送信」でまとめてお送りします。',
    linkLabel: '▼ 確認はこちら',
    url: args.url,
    body: itemLines(args.item),
  });
}

/** 「現在の送金依頼を経理に連絡するとき」の文面 (カテゴリー 1 つ分)。 */
export function buildTransferBatchNotice(args: {
  categoryName: string | null;
  items: TransferNoticeItem[];
  url: string;
}): AccountingNoticeText {
  const maker = transferMakerLabel(args.categoryName);
  const list = args.items.map((it, i) => `${circledNumber(i + 1)}${itemLines(it)}`).join('\n\n');
  return renderNotice({
    subject: `【送金依頼】${maker}へ送金をお願いします。（${args.items.length}件）`,
    title: `💸${maker}へ送金をお願いします。`,
    linkLabel: '▼ 確認はこちら',
    url: args.url,
    body: `合計金額：${formatTransferTotal(args.items)}\n\n${list}`,
    footer: `${maker}への送金、本日分は以上となります。`,
  });
}

/** 「報酬の支払い申請」のチャット文面。 */
export function buildCommissionPaymentRequestNotice(args: {
  recipientName: string;
  recipientTypeLabel: string;
  amountLabel: string;
  dealTitle: string;
  contactName: string;
  bankInfo: string;
  url: string;
}): AccountingNoticeText {
  return renderNotice({
    subject: `[報酬支払い申請] ${args.dealTitle} / ${args.recipientName} ${args.amountLabel}`,
    title: '報酬の支払い申請が届きました。',
    linkLabel: '▼ 確認はこちら',
    url: args.url,
    body: [
      `受取人：${args.recipientName}（${args.recipientTypeLabel}）`,
      `報酬額：${args.amountLabel}`,
      `商談：${args.dealTitle}`,
      `顧客：${args.contactName}`,
      `振込先：${args.bankInfo}`,
    ].join('\n'),
  });
}

/** 「SA 請求書の受領」のチャット文面。 */
export function buildCommissionInvoiceReceivedNotice(args: {
  saName: string;
  invoiceNumber: string;
  periodLabel: string;
  lineCount: number;
  totalLabel: string;
  url: string;
}): AccountingNoticeText {
  const lines = [`SA：${args.saName}`, `請求番号：${args.invoiceNumber}`];
  if (args.periodLabel) lines.push(`対象期間：${args.periodLabel}`);
  lines.push(`件数：${args.lineCount} 件`, `合計：${args.totalLabel}`);
  return renderNotice({
    subject: `[SA請求書] ${args.saName} ${args.invoiceNumber}`,
    title: 'SA から請求書が届きました。',
    linkLabel: '▼ 請求書はこちら',
    url: args.url,
    body: lines.join('\n'),
  });
}

/** 「SA 契約更新の対応」のチャット文面。 */
export function buildSaRenewalDueNotice(args: {
  saName: string;
  saEmail: string;
  reasonLabel: string;
  contractEndDate: string;
  daysLeft: number;
  url: string;
}): AccountingNoticeText {
  return renderNotice({
    subject: `[SA更新対応] ${args.saName}`,
    title: 'SA の契約更新の対応が必要です。',
    linkLabel: '▼ 確認はこちら',
    url: args.url,
    body: [
      `SA：${args.saName}（${args.saEmail}）`,
      `理由：${args.reasonLabel}`,
      `契約終了日：${args.contractEndDate || '-'}`,
      `残り日数：${args.daysLeft} 日`,
    ].join('\n'),
  });
}
